/**
 * Rows plus a mapping become transaction lines.
 *
 * This is the seam every source meets at. A CSV arrives here from ./csv.ts and
 * ./columns.ts; when PDF extraction is built it will produce the same
 * `StatementLine[]` from positioned text, and everything downstream — the
 * review table, the duplicate check, the import — is already written against
 * this shape and will not have to change.
 *
 * A row that cannot be read is NOT dropped quietly: it comes back in `skipped`
 * with the reason, so the review screen can show the owner what his file
 * contained that this could not use.
 */

import type { ImportStatementLine } from '../types';
import { parseAmount, type ColumnMapping } from './columns';
import { parseDateCell } from './dates';

/** Money out, or money in. A refund is not an expense. */
export type LineDirection = 'debit' | 'credit';

export interface StatementLine {
  /** 1-based line number in the file, header counted, so the owner can find it. */
  row: number;
  incurred_on: string;
  description: string;
  /** Always positive. Which way it went is `direction`. */
  amount: number;
  direction: LineDirection;
}

export interface SkippedRow {
  row: number;
  cells: string[];
  reason: string;
}

export interface BuiltStatement {
  lines: StatementLine[];
  skipped: SkippedRow[];
  /** The earliest and latest date read, or null when nothing was readable. */
  period_start: string | null;
  period_end: string | null;
  /** What the money-out lines add up to. */
  total_debits: number;
}

function money(value: number): number {
  return Math.round(value * 100) / 100;
}

/** The line as a signed figure: money out is what this reads off the mapping. */
function readAmount(
  row: readonly string[],
  mapping: ColumnMapping
): { amount: number; direction: LineDirection } | null {
  if (mapping.shape === 'debit_credit') {
    const debit = parseAmount(row[mapping.debitIndex] ?? '');
    if (debit !== null && debit !== 0) return { amount: Math.abs(debit), direction: 'debit' };
    const credit = parseAmount(row[mapping.creditIndex] ?? '');
    if (credit !== null && credit !== 0) return { amount: Math.abs(credit), direction: 'credit' };
    return null;
  }
  const value = parseAmount(row[mapping.amountIndex] ?? '');
  if (value === null || value === 0) return null;
  const isDebit = value > 0 === mapping.debitIsPositive;
  return { amount: Math.abs(value), direction: isDebit ? 'debit' : 'credit' };
}

/**
 * Read every row through the mapping.
 *
 * `dataRows` is `ColumnGuess.dataRows` — the header, if there was one, is
 * already off. `mapping` is the guess as the owner left it, which is why this
 * takes the mapping rather than re-deriving one: a correction he made in the
 * UI has to be what gets read.
 */
export function buildLines(dataRows: readonly string[][], mapping: ColumnMapping): BuiltStatement {
  const lines: StatementLine[] = [];
  const skipped: SkippedRow[] = [];
  const offset = mapping.hasHeader ? 2 : 1;
  let earliest: string | null = null;
  let latest: string | null = null;
  let total = 0;

  for (let i = 0; i < dataRows.length; i++) {
    const cells = dataRows[i];
    const row = i + offset;

    const incurred_on = mapping.dateIndex >= 0
      ? parseDateCell(cells[mapping.dateIndex] ?? '', mapping.dateOrder)
      : null;
    if (incurred_on === null) {
      skipped.push({ row, cells: [...cells], reason: 'No date could be read on this row.' });
      continue;
    }

    const value = readAmount(cells, mapping);
    if (value === null) {
      skipped.push({ row, cells: [...cells], reason: 'No amount could be read on this row.' });
      continue;
    }

    const description =
      mapping.descriptionIndex >= 0 ? (cells[mapping.descriptionIndex] ?? '').trim() : '';

    lines.push({ row, incurred_on, description, amount: money(value.amount), direction: value.direction });
    if (earliest === null || incurred_on < earliest) earliest = incurred_on;
    if (latest === null || incurred_on > latest) latest = incurred_on;
    if (value.direction === 'debit') total += value.amount;
  }

  return {
    lines,
    skipped,
    period_start: earliest,
    period_end: latest,
    total_debits: money(total)
  };
}

/** A review row exactly as far as `toImportLine` needs to see it — the
 *  screen's own `ReviewRow` carries more (a duplicate check result, an
 *  include tick already resolved into `chosen`), none of which changes how a
 *  row becomes a wire line. */
export interface ReviewedLine {
  line: StatementLine;
  /** The chosen category id as a string, or '' for none. Ignored on a
   *  credit — see below. */
  category: string;
  /** What the owner typed, separate from `line.description` (the statement's
   *  own text, which travels as `merchant` — see below). */
  description: string;
  hadGst: boolean;
  /** The chosen job site id as a string, or '' for company-wide. */
  jobSite: string;
}

/**
 * One reviewed row, translated into what `POST /admin/statements` expects.
 *
 * Pulled out of the review screen's commit step so this mapping — which
 * direction a row lands on, and that a credit carries no category — is
 * something the test suite can hold, not something only a click-through can
 * exercise. It is also where the credit-routing bug lived once: the screen
 * built this object inline and simply forgot to set `direction` at all,
 * which the server's default-to-'debit' turned into a ticked deposit being
 * written to `expense` instead of `income`.
 */
export function toImportLine(row: ReviewedLine): ImportStatementLine {
  return {
    incurred_on: row.line.incurred_on,
    // The statement's own text stays in `merchant`, untouched: it is what
    // the duplicate check keys on, and on a credit it is what the server
    // writes to income.source_label — same one column either way.
    merchant: row.line.description,
    description: row.description,
    // Income has no category; a credit line's category is never sent, even
    // if one was picked before the owner ticked "money in" for that row.
    category_id: row.line.direction === 'credit' ? null : row.category === '' ? null : Number(row.category),
    amount_total: row.line.amount,
    had_gst: row.hadGst,
    counts_toward_total: true,
    direction: row.line.direction,
    job_site_id: row.jobSite === '' ? null : Number(row.jobSite)
  };
}
